import { useCallback, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { useAppwideDrawerContext } from '../../../providers/AppwideDrawerProvider';
import { Button, ButtonVariants } from '../../Button';
import { Divider } from '../../Divider';
import { DrawerFooterFlex } from '../../Drawer/styles';

export function preventDrawerFormNavigation(event: FormEvent) {
  event.preventDefault();
}

export function getEntityAdminDrawerRootFormProps(suppressDrawerFormSubmit?: boolean) {
  if (suppressDrawerFormSubmit) {
    return {};
  }

  return {
    as: 'form' as const,
    onSubmit: preventDrawerFormNavigation,
  };
}

export const EntityAdminDrawerFooter = ({
  handleOnSave,
  someError,
  runValidation,
  suppressDrawerFormSubmit,
}: {
  handleOnSave: () => Promise<void>;
  someError: boolean;
  runValidation: () => void;
  suppressDrawerFormSubmit?: boolean;
}) => {
  const { closeDrawer, contentSpec } = useAppwideDrawerContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(() => {
    (contentSpec?.onClose ?? closeDrawer)();
  }, [closeDrawer, contentSpec?.onClose]);

  const handleSave = useCallback(async () => {
    if (isLoading) {
      return;
    }

    flushSync(() => setIsLoading(true));
    try {
      await handleOnSave();
    } catch (error) {
      // Error handled upstream; catch prevents unhandled promise rejection
    } finally {
      setIsLoading(false);
    }
  }, [handleOnSave, isLoading]);

  return (
    <DrawerFooterFlex w="100%" gap="spacing12" p="spacing12">
      <Button flex="none" onClick={handleCancel} data-testid="entity-admin-drawer-cancel-button">
        Cancel
      </Button>
      <Divider flex="none" orientation="vertical" />
      <Button
        type={suppressDrawerFormSubmit ? 'button' : 'submit'}
        flex="1"
        onClick={handleSave}
        variant={ButtonVariants.Primary}
        data-testid="entity-admin-drawer-save-button"
        disabled={someError}
        loading={isLoading}
        onMouseOver={runValidation}
      >
        Save
      </Button>
    </DrawerFooterFlex>
  );
};
